import React, { useCallback, useEffect, useState } from 'react';
import { Button, Card, Icon, Label, Message, Segment } from 'semantic-ui-react';
import { API, showError, showSuccess } from '../helpers';

const CodexAuthSetting = () => {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);

  const loadStatus = useCallback(async () => {
    try {
      const res = await API.get('/api/codex-chatgpt-auth/status');
      if (res.data?.success) {
        setStatus(res.data.data);
      } else {
        showError(res.data?.message || '无法读取 ChatGPT 登录状态');
      }
    } catch (error) {
      showError(error.message || '无法读取 ChatGPT 登录状态');
    }
  }, []);

  useEffect(() => {
    loadStatus();
    const timer = setInterval(loadStatus, 4000);
    return () => clearInterval(timer);
  }, [loadStatus]);

  const startLogin = async () => {
    setBusy(true);
    try {
      const res = await API.post('/api/codex-chatgpt-auth/login');
      if (res.data?.success) {
        setStatus(res.data.data);
        if (!res.data.data?.logged_in) {
          showSuccess('登录流程已启动');
        }
      } else {
        showError(res.data?.message || '无法启动 ChatGPT 登录');
      }
    } catch (error) {
      showError(error.message || '无法启动 ChatGPT 登录');
    } finally {
      setBusy(false);
      loadStatus();
    }
  };

  const logout = async () => {
    if (!window.confirm('从服务器移除 ChatGPT 登录凭据？')) return;
    setBusy(true);
    try {
      const res = await API.post('/api/codex-chatgpt-auth/logout');
      if (res.data?.success) {
        showSuccess('已移除服务器登录凭据');
        await loadStatus();
      } else {
        showError(res.data?.message || '退出登录失败');
      }
    } catch (error) {
      showError(error.message || '退出登录失败');
    } finally {
      setBusy(false);
    }
  };

  const copyTunnelCommand = async () => {
    try {
      await navigator.clipboard.writeText(status.ssh_tunnel_command);
      showSuccess('SSH 隧道命令已复制');
    } catch (error) {
      showError('复制失败，请手动复制 SSH 命令');
    }
  };

  const isLoggedIn = Boolean(status?.logged_in);
  const isLoggingIn = Boolean(status?.login_in_progress);

  return (
    <Card fluid className='chart-card'>
      <Card.Content>
        <Card.Header className='header'>ChatGPT 服务端登录</Card.Header>
        <Card.Description>
          <p>
            服务端使用已授权的 ChatGPT 账号连接 Codex 上游。登录通过 OpenAI
            官方授权页完成；OneAPI 不会收集或保存 ChatGPT 密码。
          </p>
          <Segment>
            <Label color={isLoggedIn ? 'green' : isLoggingIn ? 'orange' : 'grey'}>
              {isLoggedIn ? '已登录' : isLoggingIn ? '等待授权' : '未登录'}
            </Label>
            {status?.account_id_set && (
              <span style={{ marginLeft: 8, color: '#666' }}>
                ChatGPT 账号信息已读取
              </span>
            )}
            {status?.last_modified > 0 && (
              <p style={{ marginTop: 12 }}>
                登录缓存更新时间：{new Date(status.last_modified * 1000).toLocaleString()}
              </p>
            )}
          </Segment>

          {status?.error && (
            <Message negative>
              <Message.Header>登录未完成</Message.Header>
              <p>{status.error}</p>
            </Message>
          )}

          {isLoggingIn && (
            <Message info>
              <Message.Header>请在 OpenAI 页面完成授权</Message.Header>
              <p>
                先在你电脑上打开一个终端，运行下方 SSH 隧道命令并保持窗口打开；然后点击登录链接，选择要用于服务端的 ChatGPT 账号完成授权。
                OneAPI 不会收集或保存 ChatGPT 密码。
              </p>
              {status.verification_url && (
                <p>
                  <a href={status.verification_url} target='_blank' rel='noreferrer'>
                    打开 OpenAI 登录链接 <Icon name='external' />
                  </a>
                </p>
              )}
              {status.ssh_tunnel_command && (
                <p style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <code>{status.ssh_tunnel_command}</code>
                  <Button size='mini' onClick={copyTunnelCommand}>
                    <Icon name='copy' />复制 SSH 命令
                  </Button>
                </p>
              )}
              <p>授权完成后，此页面会自动刷新登录状态。</p>
            </Message>
          )}

          <Button primary disabled={busy || isLoggingIn || isLoggedIn} onClick={startLogin}>
            <Icon name={isLoggedIn ? 'check' : 'sign-in'} />
            {isLoggedIn ? '已登录' : '开始 OpenAI 登录'}
          </Button>
          {isLoggedIn && (
            <Button disabled={busy} onClick={logout}>
              <Icon name='sign-out' />从服务器退出
            </Button>
          )}
        </Card.Description>
      </Card.Content>
    </Card>
  );
};

export default CodexAuthSetting;
